/**
 * PROTECTED ROUTES
 * ================
 * Every authenticated page, as { path, Component }. App.js renders each one as
 * <Route path={path} element={<ProtectedRoute element={<Suspense><Component /></Suspense>} />} />.
 *
 * Pages are code-split (Stage 2.3): each page module is its own chunk, downloaded the first time
 * one of its routes renders. ProtectedRoute still decides first, so unauthenticated visitors are
 * redirected without loading any page code.
 *
 * Access to these pages (menu visibility and read/create/update/delete) is decided by the
 * GET /api/sidebar response, not here; the backend enforces it.
 */
import { PATHS } from "./paths"
import lazyPage from "./lazyPage"

// Administration
const AdminUserList = lazyPage(() => import("../pages/AdminUserList"))
const AdminAddUser = lazyPage(() => import("../pages/AdminAddUser"))
const Dashboard = lazyPage(() => import("../pages/Dashboard"))
const CompanyCurrencyList = lazyPage(() => import("../pages/CompanyCurrencyList"))
const CompanyAddCurrency = lazyPage(() => import("../pages/CompanyAddCurrency"))
const CompanyEditCurrency = lazyPage(() => import("../pages/CompanyEditCurrency"))
const CompanyCurrencyHistory = lazyPage(() => import("../pages/CompanyCurrencyHistory"))
const CompanyTaxesList = lazyPage(() => import("../pages/CompanyTaxesList"))
const CompanyAddTax = lazyPage(() => import("../pages/CompanyAddTax"))
const CompanyEditTax = lazyPage(() => import("../pages/CompanyEditTax"))
const CompanyContactMessages = lazyPage(() => import("../pages/CompanyContactMessages"))
const CompanyTerms = lazyPage(() => import("../pages/CompanyTerms"))
const CompanyPoliciesEditor = lazyPage(() => import("../pages/CompanyPoliciesEditor"))
const CompanySupport = lazyPage(() => import("../pages/CompanySupport"))
const EditUser = lazyPage(() => import("../pages/AdminEditUser"))

// Ship and Trip
const CompanyShipsList = lazyPage(() => import("../pages/CompanyShipsList"))
const CompanyAddShip = lazyPage(() => import("../pages/CompanyAddShip"))
const CompanyEditShip = lazyPage(() => import("../pages/CompanyEditShip"))
const CompanyTripsList = lazyPage(() => import("../pages/CompanyTripsList"))
const CompanyAddTrip = lazyPage(() => import("../pages/CompanyAddTrip"))
const CompanyEditTrip = lazyPage(() => import("../pages/CompanyEditTrip"))

// Partner
const CompanyPromotionsList = lazyPage(() => import("../pages/CompanyPromotionsList"))
const CompanyAddPromotion = lazyPage(() => import("../pages/CompanyAddPromotion"))
const CompanyEditPromotion = lazyPage(() => import("../pages/CompanyEditPromotion"))
const AllocationListPage = lazyPage(() => import("../pages/AllocationListPage"))
const AllocateToChildPage = lazyPage(() => import("../pages/AllocateToChildPage"))
const BusinessPartnersPage = lazyPage(() => import("../pages/BusinessPartners"))
const B2CCustomersPage = lazyPage(() => import("../pages/B2CCustomersPage"))
const SalesmenPage = lazyPage(() => import("../pages/SalesmenPage"))
const MarkupDiscountBoardPage = lazyPage(() => import("../pages/MarkupDiscountBoardPage"))
const AddRulePage = lazyPage(() => import("../pages/AddRulePage"))
const EditRulePage = lazyPage(() => import("../pages/EditRulePage"))
const CommissionBoardPage = lazyPage(() => import("../pages/CommissionBoardPage"))
const AddCommissionPage = lazyPage(() => import("../pages/AddCommissionPage"))
const EditCommissionPage = lazyPage(() => import("../pages/EditCommissionPage"))

// Sales and Booking
const PriceListPage = lazyPage(() => import("../pages/PriceListPage"))
const TicketingRulesPage = lazyPage(() => import("../pages/TicketingRulesPage"))
const AddTicketRulePage = lazyPage(() => import("../pages/AddTicketRulePage"))
const BookingAndTicketsPage = lazyPage(() => import("../pages/BookingAndTicketsPage"))
const CreateBookingPage = lazyPage(() => import("../pages/CreateBookingPage"))
const ExcessBaggageTickets = lazyPage(() => import("../pages/ExcessBaggageTickets"))

// Checking and Boarding
const PassengerCheckingPage = lazyPage(() => import("../pages/PassengerCheckingPage"))
const CargoCheckingPage = lazyPage(() => import("../pages/CargoCheckingPage"))
const VehicleCheckingPage = lazyPage(() => import("../pages/VehicleCheckingPage"))
const CargoBoardingPage = lazyPage(() => import("../pages/CargoBoardingPage"))
const PassengerBoardingPage = lazyPage(() => import("../pages/PassengerBoardingPage"))
const VehicleBoardingPage = lazyPage(() => import("../pages/VehicleBoardingPage"))
const TripCompletionPage = lazyPage(() => import("../pages/TripCompletionPage"))

// Finance
const FinancePage = lazyPage(() => import("../pages/FinancePage"))
const BankAndCashAccountsPage = lazyPage(() => import("../pages/BankAndCashAccountsPage"))
const ChartOfAccountsPage = lazyPage(() => import("../pages/ChartOfAccountsPage"))
const JournalEntries = lazyPage(() => import("../pages/JournalEntries"))
const AddNewJournalEntry = lazyPage(() => import("../pages/AddNewJournalEntry"))
const EditJournalEntry = lazyPage(() => import("../pages/EditJournalEntry"))
const AgentTopup = lazyPage(() => import("../pages/AgentTopup"))
const AddTopup = lazyPage(() => import("../pages/AddTopup"))
const PaymentsAndReceipts = lazyPage(() => import("../pages/PaymentsAndReceipts"))
const AddInternalPaymentReceipt = lazyPage(() => import("../pages/AddInternalPaymentReceipt"))
const GeneralLedger = lazyPage(() => import("../pages/GeneralLedger"))
const TrialBalance = lazyPage(() => import("../pages/TrialBalance"))

// Settings
const CompanyProfile = lazyPage(() => import("../pages/CompanyProfile"))
const CompanyEditProfile = lazyPage(() => import("../pages/EditCompanyProfile"))
const CompanyProfileList = lazyPage(() => import("../pages/CompanyProfileList"))
const RolePermission = lazyPage(() => import("../pages/RolePermission"))
const AddGroupPermission = lazyPage(() => import("../pages/AddGroupPermission"))
const Port = lazyPage(() => import("../pages/Port"))
const AddPort = lazyPage(() => import("../pages/AddPort"))
const Cabin = lazyPage(() => import("../pages/Cabin"))
const AddCabin = lazyPage(() => import("../pages/AddCabin"))
const EditCabin = lazyPage(() => import("../pages/EditCabin"))
const PayloadType = lazyPage(() => import("../pages/PayloadType"))
const SystemAlerts = lazyPage(() => import("../pages/SystemAlerts"))
const EditPort = lazyPage(() => import("../pages/EditPort"))

export const PROTECTED_ROUTES = [
  // Dashboard
  { path: PATHS.dashboard, Component: Dashboard },

  // Administration
  { path: PATHS.addUser, Component: AdminAddUser },
  { path: PATHS.editCompanyProfile, Component: CompanyEditProfile },
  { path: PATHS.userList, Component: AdminUserList },
  { path: PATHS.currencies, Component: CompanyCurrencyList },
  { path: PATHS.addCurrency, Component: CompanyAddCurrency },
  { path: PATHS.editCurrency, Component: CompanyEditCurrency },
  { path: PATHS.editUser, Component: EditUser },
  { path: PATHS.currencyHistory, Component: CompanyCurrencyHistory },
  { path: PATHS.taxes, Component: CompanyTaxesList },
  { path: PATHS.addTax, Component: CompanyAddTax },
  { path: PATHS.editTax, Component: CompanyEditTax },
  { path: PATHS.contactMessages, Component: CompanyContactMessages },
  { path: PATHS.terms, Component: CompanyTerms },
  { path: PATHS.policies, Component: CompanyPoliciesEditor },
  { path: PATHS.support, Component: CompanySupport },

  // Ships and Trips
  { path: PATHS.ships, Component: CompanyShipsList },
  { path: PATHS.addShip, Component: CompanyAddShip },
  { path: PATHS.editShip, Component: CompanyEditShip },
  { path: PATHS.trips, Component: CompanyTripsList },
  { path: PATHS.addTrip, Component: CompanyAddTrip },
  { path: PATHS.editTrip, Component: CompanyEditTrip },

  // Partners
  { path: PATHS.promotions, Component: CompanyPromotionsList },
  { path: PATHS.addPromotion, Component: CompanyAddPromotion },
  { path: PATHS.editPromotion, Component: CompanyEditPromotion },
  { path: PATHS.partners, Component: BusinessPartnersPage },
  { path: PATHS.b2cCustomers, Component: B2CCustomersPage },
  { path: PATHS.salesmen, Component: SalesmenPage },
  { path: PATHS.markup, Component: MarkupDiscountBoardPage },
  { path: PATHS.addMarkupRule, Component: AddRulePage },
  { path: PATHS.editMarkupRule, Component: EditRulePage },
  { path: PATHS.commission, Component: CommissionBoardPage },
  { path: PATHS.addCommissionRule, Component: AddCommissionPage },
  { path: PATHS.editCommissionRule, Component: EditCommissionPage },

  // Allocation
  { path: PATHS.allocations, Component: AllocationListPage },
  { path: PATHS.allocateToChild, Component: AllocateToChildPage },
  { path: PATHS.viewAllocation, Component: AllocateToChildPage },

  // Sales and Booking
  { path: PATHS.priceList, Component: PriceListPage },
  { path: PATHS.ticketingRules, Component: TicketingRulesPage },
  { path: PATHS.addTicketRule, Component: AddTicketRulePage },
  { path: PATHS.bookingsAndTickets, Component: BookingAndTicketsPage },
  { path: PATHS.createBooking, Component: CreateBookingPage },
  { path: PATHS.excessBaggageTickets, Component: ExcessBaggageTickets },

  // Checking and Boarding
  { path: PATHS.passengerChecking, Component: PassengerCheckingPage },
  { path: PATHS.cargoChecking, Component: CargoCheckingPage },
  { path: PATHS.vehicleChecking, Component: VehicleCheckingPage },
  { path: PATHS.cargoBoarding, Component: CargoBoardingPage },
  { path: PATHS.passengerBoarding, Component: PassengerBoardingPage },
  { path: PATHS.vehicleBoarding, Component: VehicleBoardingPage },
  { path: PATHS.tripCompletion, Component: TripCompletionPage },

  // Finance
  { path: PATHS.finance, Component: FinancePage },
  { path: PATHS.bankCashAccounts, Component: BankAndCashAccountsPage },
  { path: PATHS.chartOfAccounts, Component: ChartOfAccountsPage },
  { path: PATHS.journalEntries, Component: JournalEntries },
  { path: PATHS.addJournalEntry, Component: AddNewJournalEntry },
  { path: PATHS.editJournalEntry, Component: EditJournalEntry },
  { path: PATHS.agentTopUps, Component: AgentTopup },
  { path: PATHS.addTopUp, Component: AddTopup },
  { path: PATHS.paymentsReceipts, Component: PaymentsAndReceipts },
  { path: PATHS.addInternalPaymentReceipt, Component: AddInternalPaymentReceipt },
  { path: PATHS.generalLedger, Component: GeneralLedger },
  { path: PATHS.trialBalance, Component: TrialBalance },

  // Settings
  { path: PATHS.companyProfile, Component: CompanyProfile },
  { path: PATHS.companyProfileList, Component: CompanyProfileList },
  { path: PATHS.rolePermissions, Component: RolePermission },
  { path: PATHS.addGroupPermission, Component: AddGroupPermission },
  { path: PATHS.ports, Component: Port },
  { path: PATHS.addPort, Component: AddPort },
  { path: PATHS.editPort, Component: EditPort },
  { path: PATHS.cabins, Component: Cabin },
  { path: PATHS.editCabin, Component: EditCabin },
  { path: PATHS.addCabin, Component: AddCabin },
  { path: PATHS.payloadTypes, Component: PayloadType },

  // System Alerts
  { path: PATHS.systemAlerts, Component: SystemAlerts },
]
